// Cuestionario de antecedentes médicos — calcado de la ficha en papel del cliente
// ("FICHA MÉDICA - ODONTOLÓGICA"). El orden de QUESTIONNAIRE_ITEMS_DEF es el orden
// impreso en esa ficha; no reordenar (el consultorio puede reordenar/ocultar desde
// Configuración > Campos personalizados, ver formsConfig.ts).

import { applyOrder, type CustomQuestionType, type FormSectionConfig } from "./formsConfig";

export type YesNo = "yes" | "no" | null;

export type QuestionnaireItem = {
  key: string;
  label: string;
  /** "yesno" (default histórico) o "text" (preguntas custom de texto libre). */
  type: CustomQuestionType;
  answer: YesNo;
  detail: string;
};

export type MedicalQuestionnaire = {
  version: 1;
  underMedicalCare: YesNo;
  currentMedication: { answer: YesNo; detail: string };
  items: QuestionnaireItem[];
  tetanusVaccine: { answer: YesNo; date: string };
  pregnant: YesNo;
  breastfeeding: YesNo;
  otherConditions: string;
};

export const QUESTIONNAIRE_ITEMS_DEF: { key: string; label: string }[] = [
  { key: "allergyAnesthetics", label: "Alergias a anestésicos" },
  { key: "allergyDrugs", label: "Alergias a Drogas" },
  { key: "tonsillitis", label: "Amigdalitis" },
  { key: "anemia", label: "Anemia" },
  { key: "arthritis", label: "Artritis" },
  { key: "asthma", label: "Asma" },
  { key: "seizures", label: "Convulsiones" },
  { key: "diabetes", label: "Diabetes" },
  { key: "heartConditions", label: "Afecciones cardíacas" },
  { key: "heartDisease", label: "Enfermedades cardíacas" },
  { key: "drugAddiction", label: "Adicción a drogas" },
  { key: "hivCarrier", label: "Portador H.I.V." },
  { key: "rheumaticFever", label: "Fiebre Reumática" },
  { key: "hepatitis", label: "Hepatitis" },
  { key: "neurological", label: "Alt. Neurológicas" },
  { key: "ocular", label: "Alt. Oculares" },
  { key: "renal", label: "Alt. Renales" },
  { key: "sinusitis", label: "Sinusitis" },
  { key: "bleeding", label: "Hemorragia en heridas" },
  { key: "thyroid", label: "Tiroides" },
  { key: "tuberculosis", label: "Tuberculosis" },
  { key: "gastricUlcer", label: "Úlcera Gastroduodenal" },
  { key: "hypertension", label: "Hipertensión Sanguínea" },
  { key: "hypotension", label: "Hipotensión Sanguínea" },
  { key: "osteoporosis", label: "Osteoporosis" },
];

type QuestionnaireItemDef = { key: string; label: string; type: CustomQuestionType };

/** Lista efectiva de ítems del cuestionario para un consultorio: defaults sin los
 *  ocultados en `config.hiddenDefaultIds`, más `config.customItems`, en el orden de
 *  `config.order` (lo que falte al final, en su orden natural). */
export function resolveQuestionnaireItemDefs(config?: FormSectionConfig): QuestionnaireItemDef[] {
  const hidden = new Set(config?.hiddenDefaultIds ?? []);
  const defaults: QuestionnaireItemDef[] = QUESTIONNAIRE_ITEMS_DEF.filter((d) => !hidden.has(d.key)).map((d) => ({
    key: d.key,
    label: d.label,
    type: "yesno",
  }));
  const custom: QuestionnaireItemDef[] = (config?.customItems ?? []).map((c) => ({
    key: c.id,
    label: c.label,
    type: c.type,
  }));
  return applyOrder([...defaults, ...custom], (d) => d.key, config?.order);
}

export function emptyMedicalQuestionnaire(config?: FormSectionConfig): MedicalQuestionnaire {
  return {
    version: 1,
    underMedicalCare: null,
    currentMedication: { answer: null, detail: "" },
    items: resolveQuestionnaireItemDefs(config).map((def) => ({ ...def, answer: null, detail: "" })),
    tetanusVaccine: { answer: null, date: "" },
    pregnant: null,
    breastfeeding: null,
    otherConditions: "",
  };
}

/** Normaliza un valor recibido de la API (o null) a un MedicalQuestionnaire completo,
 *  resolviendo los ítems contra la config del consultorio y rellenando cualquier ítem
 *  faltante con su default vacío. */
export function parseMedicalQuestionnaire(raw: unknown, config?: FormSectionConfig): MedicalQuestionnaire {
  const empty = emptyMedicalQuestionnaire(config);
  if (!raw || typeof raw !== "object") return empty;
  const r = raw as Partial<MedicalQuestionnaire>;

  const itemByKey = new Map((r.items ?? []).map((it) => [it.key, it]));
  const items = resolveQuestionnaireItemDefs(config).map((def) => {
    const found = itemByKey.get(def.key);
    return {
      key: def.key,
      label: def.label,
      type: def.type,
      answer: found?.answer ?? null,
      detail: found?.detail ?? "",
    };
  });

  return {
    version: 1,
    underMedicalCare: r.underMedicalCare ?? null,
    currentMedication: {
      answer: r.currentMedication?.answer ?? null,
      detail: r.currentMedication?.detail ?? "",
    },
    items,
    tetanusVaccine: {
      answer: r.tetanusVaccine?.answer ?? null,
      date: r.tetanusVaccine?.date ?? "",
    },
    pregnant: r.pregnant ?? null,
    breastfeeding: r.breastfeeding ?? null,
    otherConditions: r.otherConditions ?? "",
  };
}

/** true si el paciente contestó al menos algo del cuestionario nuevo. */
export function hasQuestionnaireContent(q: MedicalQuestionnaire): boolean {
  return (
    q.underMedicalCare !== null ||
    q.currentMedication.answer !== null ||
    q.currentMedication.detail.trim() !== "" ||
    q.items.some((it) => it.answer !== null || it.detail.trim() !== "") ||
    q.tetanusVaccine.answer !== null ||
    q.tetanusVaccine.date.trim() !== "" ||
    q.pregnant !== null ||
    q.breastfeeding !== null ||
    q.otherConditions.trim() !== ""
  );
}
